import { useCallback, useMemo, useState } from "react";
import { buildTurnTimes } from "./traceTurnTimes.js";
import { useTraceReadDwell } from "./traceReadDwell.js";

const UNKNOWN = { bound: "unknown", startTs: null, endTs: null, eventId: null, fromTurn: null, toTurn: null };

// Message identities establish dates. Visibility controls the corroboration
// work those dates can trigger; it never reconstructs or parses a request.
export function useTurnClock({ turns = [], sessionId = "", focal = null, enabled = true }) {
  const key = `${sessionId}:${focal?.eventId || ""}:` + turns.map(t => `${t.messageId || ""}:${t.messageAt ?? ""}`).join(";");
  const ready = useTraceReadDwell(key, enabled);
  const [requests, setRequests] = useState(() => ({ key, asked: new Set() }));
  const requestTurn = useCallback(index => {
    if (!ready || !Number.isInteger(index) || index < 0 || index >= turns.length) return;
    setRequests(previous => {
      if (previous.key === key && previous.asked.has(index)) return previous;
      return { key, asked: new Set([...(previous.key === key ? previous.asked : []), index]) };
    });
  }, [key, ready, turns.length]);
  return useMemo(() => {
    const times = enabled ? buildTurnTimes(turns)?.times ?? [] : [];
    const readings = new Map();
    let previous = null;
    for (let start = 0; start < times.length; start += 1) {
      const at = times[start]; if (!Number.isFinite(at)) continue;
      let end = start;
      while (end + 1 < times.length && times[end + 1] === at) end += 1;
      const reading = { bound: previous == null ? "before" : "window", startTs: previous, endTs: at,
        eventId: turns[start].observationSourceEventId || null, fromTurn: start, toTurn: end };
      for (let index = start; index <= end; index += 1) readings.set(index, reading);
      previous = at; start = end;
    }
    const observed = times.filter(Number.isFinite);
    const asked = ready && requests.key === key ? requests.asked : new Set();
    const dated = [...asked].filter(index => readings.has(index)).length;
    return {
      ready: enabled && ready,
      span: ready && observed.at(-1) > observed[0] ? { fromTs: observed[0], toTs: observed.at(-1) } : null,
      requestTurn, timeOf: index => asked.has(index) ? readings.get(index) || UNKNOWN : UNKNOWN,
      coverage: { asked: asked.size, dated, rungs: new Set(readings.values()).size, stats: null },
      clockCoverage: { dated: readings.size, total: turns.length, rungs: new Set(readings.values()).size },
      busy: false, inFlight: 0, loadingPriors: false,
    };
  }, [turns, enabled, ready, requests, key, requestTurn]);
}
